import React, { useRef } from "react";
import { S } from "../styles/tokens";
import PricingCards from "../components/PricingCards";
import { useEnter, useReveal, useCountUp, useTilt, smoothScrollTo } from "../lib/motion";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

function StatItem(props) {
  var item = props.item;
  var numRef = useRef(null);
  useCountUp(numRef, item[0]);
  return (
    <div style={{ padding: "28px 24px", textAlign: "center", borderRight: props.isLast ? "none" : "1px solid rgba(247,243,238,.1)" }}>
      <div ref={numRef} style={{ fontFamily: "Georgia,serif", fontSize: 44, fontWeight: 500, color: "#c9963a", lineHeight: 1 }}>{item[0]}</div>
      <div style={{ fontSize: 11, letterSpacing: 2, color: "#f7f3ee", marginTop: 6, fontWeight: 600 }}>{item[1]}</div>
      <div style={{ fontSize: 11, color: "rgba(247,243,238,.5)", marginTop: 4 }}>{item[2]}</div>
    </div>
  );
}

function StepCard(props) {
  var item = props.item;
  var tiltRef = useTilt({ maxTilt: 6 });
  return (
    <div ref={tiltRef} style={{ background: "#fefcfa", border: "1px solid rgba(180,160,140,.28)", borderRadius: 20, padding: "32px 28px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: "#a07828", fontWeight: 700 }}>{item.step}</div>
        <div style={{ fontSize: 28 }}>{item.icon}</div>
      </div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: 18, fontWeight: 500, marginBottom: 10, color: "#2c2420" }}>{item.title}</div>
      <div style={{ fontSize: 13, color: "#6b5e56", lineHeight: 1.75 }}>{item.desc}</div>
    </div>
  );
}

function FeatureCard(props) {
  var f = props.f;
  var tiltRef = useTilt({ maxTilt: 6 });
  return (
    <div ref={tiltRef} style={{ background: "#fefcfa", border: "1px solid rgba(180,160,140,.28)", borderRadius: 16, padding: "24px 22px" }}>
      <div style={{ fontSize: 28, marginBottom: 12 }}>{f.icon}</div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: 16, fontWeight: 500, marginBottom: 8, color: "#2c2420" }}>{f.title}</div>
      <div style={{ fontSize: 12, color: "#6b5e56", lineHeight: 1.7 }}>{f.desc}</div>
    </div>
  );
}

function TestimonialCard(props) {
  var t = props.t;
  var tiltRef = useTilt({ maxTilt: 5 });
  return (
    <div ref={tiltRef} style={{ background: "#fefcfa", border: "1px solid rgba(180,160,140,.28)", borderRadius: 20, padding: "28px 24px" }}>
      <div style={{ fontSize: 32, color: "#c9963a", marginBottom: 12, fontFamily: "Georgia,serif" }}>"</div>
      <div style={{ fontSize: 13, color: "#4a3f38", lineHeight: 1.8, marginBottom: 20 }}>{t.quote}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#2c2420", display: "flex", alignItems: "center", justifyContent: "center", color: "#c9963a", fontFamily: "Georgia,serif", fontSize: 16, fontWeight: 500 }}>{t.name[0]}</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#2c2420" }}>{t.name}</div>
          <div style={{ fontSize: 11, color: "#7a6e64" }}>{t.role}</div>
        </div>
      </div>
    </div>
  );
}

export default function LandingView(props) {
  var user = props.user;
  var userPlan = props.userPlan;
  var onNavigate = props.onNavigate;

  var heroRef = useRef(null);
  var videoRef = useRef(null);
  var statsRef = useRef(null);
  var howRef = useRef(null);
  var featRef = useRef(null);
  var testiRef = useRef(null);
  var trustRef = useRef(null);
  var footerRef = useRef(null);

  // Hero content enters on mount; background video drifts slowly on scroll.
  useEnter(heroRef, { selector: ".hero-anim", stagger: 0.09, y: 26 });
  useGSAP(function() {
    if (!videoRef.current) return;
    gsap.to(videoRef.current, {
      yPercent: 12,
      ease: "none",
      scrollTrigger: { trigger: heroRef.current, start: "top top", scrub: 0.5 },
    });
  }, { scope: heroRef });

  useReveal(statsRef, { stagger: 0.06, y: 16 });
  useReveal(howRef, { stagger: 0.1 });
  useReveal(featRef, { stagger: 0.07 });
  useReveal(testiRef, { stagger: 0.1 });
  useReveal(trustRef, { selector: "span", stagger: 0.04, y: 10, duration: 0.4 });
  useReveal(footerRef, { stagger: 0.08 });

  return (
    <div style={{ paddingBottom: 100 }}>

      {/* HERO with background video + 3D gem */}
      <div ref={heroRef} style={{ position: "relative", overflow: "hidden" }}>

        {/* background video */}
        <video
          ref={function(el) { videoRef.current = el; if (el) el.muted = true; }}
          autoPlay loop muted playsInline preload="auto"
          poster="/hero-poster.jpg"
          style={{ position: "absolute", inset: 0, width: "100%", height: "112%", objectFit: "cover", zIndex: 0 }}
        >
          <source src="/hero-bg.mp4" type="video/mp4" />
        </video>

        {/* cream scrim — keeps the dark hero text readable. Lower these alphas to let the video show more. */}
        <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(180deg, rgba(247,243,238,0.5) 0%, rgba(247,243,238,0.38) 45%, rgba(247,243,238,0.62) 100%)" }} />

        <div
          className="hero-inner"
          style={{ ...S.wrap, position: "relative", zIndex: 2, paddingTop: 90, paddingBottom: 80, textAlign: "center" }}
        >
          <div className="hero-anim" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 10, letterSpacing: 4, color: "#c9963a", border: "1px solid rgba(201,150,58,.3)", padding: "5px 18px", borderRadius: 100, marginBottom: 26, background: "rgba(201,150,58,.05)" }}>
            ✦ AI-POWERED RESUME INTELLIGENCE
          </div>
          <h1 className="hero-anim" style={{ fontFamily: "Georgia,serif", fontSize: "clamp(38px,5.5vw,72px)", fontWeight: 500, lineHeight: 1.07, letterSpacing: -1.5, marginBottom: 22, color: "#2c2420" }}>
            Land your dream role<br />with <em style={{ color: "#a07828" }}>perfect alignment</em>
          </h1>
          <p className="hero-anim" style={{ color: "#6b5e56", fontSize: 16, lineHeight: 1.85, maxWidth: 560, margin: "0 auto 14px" }}>
            SYNTRIX analyses your resume against any job description — delivering a precise ATS score, keyword gaps, an AI-rewritten resume, and a tailored cover letter in seconds.
          </p>
          <p className="hero-anim" style={{ color: "#a07828", fontSize: 13, marginBottom: 36, letterSpacing: 0.5 }}>
            ✓ Free to start &nbsp;·&nbsp; ✓ No credit card required &nbsp;·&nbsp; ✓ Results in 30 seconds
          </p>
          <div className="hero-anim hero-btns" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button style={S.btnPrimary} onClick={function() {
              if (!user) { onNavigate("auth"); }
              else if (userPlan !== "free") { onNavigate("app"); }
              else { onNavigate("dashboard"); }
            }}>
              <svg width={13} height={13} viewBox="0 0 14 14" fill="none"><path d="M7 1L8.8 5H13L9.6 7.5L11 12L7 9.5L3 12L4.4 7.5L1 5H5.2L7 1Z" fill="currentColor" /></svg>
              {!user ? "Get Started Free" : userPlan !== "free" ? "Analyse Resume →" : "Go to Dashboard"}
            </button>
            <button style={{ ...S.btnReset, fontSize: 13, padding: "14px 28px" }} onClick={function() { smoothScrollTo("#pricing"); }}>
              View Pricing ↓
            </button>
          </div>
        </div>

      </div>

      {/* STATS */}
      <div style={{ background: "#2c2420", padding: "48px 0", marginBottom: 80 }}>
        <div style={{ ...S.wrap }}>
          <div ref={statsRef} className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 0 }}>
            {[["98%", "ATS Accuracy Rate", "Industry-leading precision"], ["40K+", "Resumes Analysed", "And counting every day"], ["3×", "More Interview Calls", "Proven by our users"], ["30s", "Average Analysis Time", "Lightning fast results"]].map(function(item, i) {
              return <StatItem key={i} item={item} isLast={i === 3} />;
            })}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div style={{ ...S.wrap, marginBottom: 80 }}>
        <div style={S.secHd}>
          <div style={S.secEye}>HOW IT WORKS</div>
          <div style={S.secTitle}>Get hired faster in 3 steps</div>
          <div style={S.secSub}>No technical skills needed. Just paste and get results.</div>
        </div>
        <div ref={howRef} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 20 }}>
          {[
            { step: "01", icon: "📋", title: "Paste Job Description", desc: "Copy the job posting from LinkedIn, Indeed, or any job board and paste it into SYNTRIX." },
            { step: "02", icon: "📄", title: "Upload Your Resume", desc: "Paste your resume text or upload a PDF, DOCX, or TXT file. We extract everything automatically." },
            { step: "03", icon: "🚀", title: "Get AI-Powered Results", desc: "Receive your ATS score, keyword gaps, rewritten resume, cover letter, and LinkedIn optimisation." },
          ].map(function(item) {
            return <StepCard key={item.step} item={item} />;
          })}
        </div>
      </div>

      {/* FEATURES */}
      <div style={{ background: "#efe8dc", padding: "72px 0", marginBottom: 80 }}>
        <div style={S.wrap}>
          <div style={S.secHd}>
            <div style={S.secEye}>FEATURES</div>
            <div style={S.secTitle}>Everything you need to get hired</div>
            <div style={S.secSub}>SYNTRIX gives you the full toolkit to beat the ATS and impress recruiters.</div>
          </div>
          <div ref={featRef} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16 }}>
            {[
              { icon: "🎯", title: "ATS Score", desc: "Get a precise 0-100 score showing how well your resume matches the job.", color: "#a07828" },
              { icon: "🔑", title: "Keyword Analysis", desc: "See exactly which keywords you're missing and which you've nailed.", color: "#5b8aaa" },
              { icon: "✍️", title: "AI Resume Rewrite", desc: "Get your entire resume rewritten and optimised for this specific job.", color: "#6b8f71" },
              { icon: "📝", title: "Cover Letter", desc: "A personalised cover letter tailored to the role and your experience.", color: "#b85c38" },
              { icon: "💼", title: "LinkedIn Optimisation", desc: "AI-rewritten headline, about section, and skills to attract recruiters.", color: "#a07828" },
              { icon: "📊", title: "Detailed Breakdown", desc: "4-category scoring: keywords, skills, experience, and formatting quality.", color: "#5b8aaa" },
            ].map(function(f) {
              return <FeatureCard key={f.title} f={f} />;
            })}
          </div>
        </div>
      </div>

      {/* TESTIMONIALS */}
      <div style={{ ...S.wrap, marginBottom: 80 }}>
        <div style={S.secHd}>
          <div style={S.secEye}>TESTIMONIALS</div>
          <div style={S.secTitle}>What our users say</div>
        </div>
        <div ref={testiRef} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20 }}>
          {[
            { quote: "My ATS score went from 42 to 87 after using SYNTRIX. Got 3 interview calls the same week!", name: "Priya S.", role: "Software Engineer" },
            { quote: "The AI cover letter was so good I barely had to edit it. Landed my dream job in 2 weeks.", name: "James T.", role: "Product Manager" },
            { quote: "I didn't know what ATS even meant before SYNTRIX. Now I actually get callbacks!", name: "Aisha M.", role: "Marketing Analyst" },
          ].map(function(t) {
            return <TestimonialCard key={t.name} t={t} />;
          })}
        </div>
      </div>

      {/* PRICING */}
      <div id="pricing" style={{ ...S.wrap, marginBottom: 60 }}>
        <div style={S.secHd}>
          <div style={S.secEye}>PRICING</div>
          <div style={S.secTitle}>Choose Your Edge</div>
          <div style={S.secSub}>Every plan includes a 7-day money-back guarantee. No contracts. Cancel anytime.</div>
        </div>
        <PricingCards user={user} onNeedAuth={function() { onNavigate("auth"); }} />
      </div>

      {/* TRUST BAR */}
      <div style={{ borderTop: "1px solid rgba(180,160,140,.16)", borderBottom: "1px solid rgba(180,160,140,.16)", padding: "24px 0", marginBottom: 60 }}>
        <div ref={trustRef} style={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 32, fontSize: 12, color: "#7a6e64", letterSpacing: 0.5 }}>
          {["🔒 SSL Encrypted", "💳 Secure Payments via Stripe", "↩ 7-Day Money-Back", "❌ No Hidden Fees", "🚫 Data Never Sold"].map(function(t) { return <span key={t}>{t}</span>; })}
        </div>
      </div>

      {/* FOOTER CTA */}
      <div ref={footerRef} style={{ ...S.wrap, textAlign: "center" }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "clamp(26px,4vw,48px)", fontWeight: 500, letterSpacing: -1, marginBottom: 16, color: "#2c2420" }}>
          Ready to land your dream job?
        </div>
        <p style={{ color: "#6b5e56", fontSize: 15, marginBottom: 32 }}>Join thousands of job seekers who use SYNTRIX to get more interviews.</p>
        <button style={S.btnPrimary} onClick={function() { !user ? onNavigate("auth") : onNavigate("app"); }}>
          <svg width={13} height={13} viewBox="0 0 14 14" fill="none"><path d="M7 1L8.8 5H13L9.6 7.5L11 12L7 9.5L3 12L4.4 7.5L1 5H5.2L7 1Z" fill="currentColor" /></svg>
          {!user ? "Start for Free — No Card Needed" : "Analyse My Resume Now"}
        </button>
      </div>

    </div>
  );
}
